<template>
  <app-page class="pl-page" fill lean>
    <!-- Handy: Aktualisieren als Icon-Knopf im Kopf (wie Rollen); sonst rechts in der Werkzeugleiste -->
    <template v-if="handy" #actions>
      <app-icon-button icon="refresh" label="Aktualisieren" :disabled="loading" @click="load(false)" />
    </template>
    <!-- Werkzeugleiste wie Verkäufe und Rollen: links Zeitraum (Tage, Schnellwahl Heute · Gestern · 7 Tage · 30 Tage) und
         Suche, rechts Drucker, Art und Aktualisieren -->
    <template #toolbar>
      <period-field
        days-only
        period-type="daily"
        :from-date="fromDate"
        :date="toDate"
        :presets="presets"
        @apply="onPeriodApply"
      />
      <app-search v-model="searchText" class="pl-search" placeholder="Im Druckprotokoll suchen" />
      <span v-if="stand && !handy" class="pl-meta">Stand {{ stand }}</span>
    </template>
    <template #toolbar-end>
      <app-select
        v-model="printerFilter"
        :items="printerFilterItems"
        icon="printer"
        prefix="Drucker"
        hide-prefix
        :menu-width="280"
      />
      <app-select
        v-model="kindFilter"
        :items="kindFilterItems"
        icon="tag"
        prefix="Art"
        hide-prefix
        :menu-width="260"
      />
      <app-button v-if="!handy" icon="refresh" :loading="loading" @click="load(false)">Aktualisieren</app-button>
    </template>

    <div class="pl-gridcol" :class="{ 'pl-gridcol--natural': handy }">
      <div class="app-grid-fill" :class="{ 'pl-gridfill--leer': !!overlayState }">
        <dx-data-grid
          ref="grid"
          :data-source="shownRows"
          key-expr="id"
          :columns="columns"
          :height="gridHeight"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="false"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :hover-state-enabled="!gridTouch"
          :focused-row-enabled="true"
          :element-attr="gridAttr"
          :scrolling="scrolling"
          no-data-text=""
          @focused-row-changed="onFocusedRowChanged"
          @content-ready="onContentReady"
        >
          <dx-sorting mode="single" :show-sort-indexes="false" />
          <dx-paging :page-size="defaultPageSize" />
          <dx-pager :visible="false" />
          <dx-selection mode="none" />

          <!-- Zeit zweizeilig: Uhrzeit mit Sekunden, darunter der Tag (mit Jahr, wenn der Zeitraum einen Jahreswechsel
               enthält) -->
          <template #zeitZelle="{ data }">
            <div class="pl-cell">
              <span class="pl-num">{{ clockText(data.data.time) }}</span>
              <span class="pl-sub">{{ dayText(data.data.time) }}</span>
            </div>
          </template>
          <!-- Art mit dezenter Kennung: Verkauf ohne Farbe, Storno rot, Druck ohne Verkauf blau, Druckerzustand grau,
               Störung und Papier leer gelb -->
          <template #artZelle="{ data }">
            <span class="pl-kind" :class="'pl-kind--' + data.data.tone" :title="data.data.kind_label">
              <span class="pl-kind__dot" aria-hidden="true"></span>
              <span class="pl-ellipsis">{{ data.data.kind_label }}</span>
            </span>
          </template>
          <!-- Bezeichnung: Fahrkarte bzw. Beschreibung des Drucks, beim Verkauf darunter der Preis. Handy: darunter
               Meldung oder Preis · Drucker · Benutzer (die übrigen Spalten stehen im Detail) -->
          <template #bezZelle="{ data }">
            <div class="pl-cell">
              <span class="pl-ellipsis pl-main" :class="{ 'pl-leer': !data.data.description }" :title="data.data.description">{{ data.data.description || leer }}</span>
              <span
                v-if="handy"
                class="pl-sub pl-ellipsis"
                :class="data.data.message_level ? 'pl-sub--' + data.data.message_level : null"
              >{{ phoneLine(data.data) }}</span>
              <span v-else-if="data.data.source === 'sale' && data.data.price !== null" class="pl-sub pl-num">{{ euro(data.data.price) }}</span>
            </div>
          </template>
          <template #druckerZelle="{ data }">
            <div class="pl-cell">
              <span class="pl-num">{{ data.data.printer_id !== null ? data.data.printer_id : leer }}</span>
              <span v-if="data.data.printer_comment" class="pl-sub pl-ellipsis" :title="data.data.printer_comment">{{ data.data.printer_comment }}</span>
            </div>
          </template>
          <template #benutzerZelle="{ data }">
            <span class="pl-ellipsis" :class="{ 'pl-leer': !userLabel(data.data) }" :title="userLabel(data.data)">{{ userLabel(data.data) || leer }}</span>
          </template>
          <!-- Ort: Haltestelle (Verkauf: Einstieg), darunter Linie und Fahrt; GPS als Link auf OpenStreetMap -->
          <template #ortZelle="{ data }">
            <div class="pl-cell">
              <span class="pl-ort">
                <span class="pl-ellipsis" :class="{ 'pl-leer': !data.data.stop_name && !data.data.osm_url }" :title="data.data.stop_name">{{ data.data.stop_name || (data.data.osm_url ? 'GPS-Position' : leer) }}</span>
                <a
                  v-if="data.data.osm_url"
                  class="pl-map"
                  :href="data.data.osm_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Auf OpenStreetMap zeigen"
                  aria-label="Auf OpenStreetMap zeigen"
                  @click.stop
                ><app-icon name="pin" :size="14" /></a>
              </span>
              <span v-if="data.data.line_trip" class="pl-sub pl-ellipsis">{{ data.data.line_trip }}</span>
            </div>
          </template>
          <!-- Rolle/Abschnitte kompakt „034825 · 120–125“, darunter die Papierlänge bzw. bei Meldungen „gelesene Stelle“ -->
          <template #rolleZelle="{ data }">
            <div class="pl-cell">
              <span class="pl-ellipsis pl-num" :class="{ 'pl-leer': !data.data.roll_text }" :title="data.data.roll_text">{{ data.data.roll_text || leer }}</span>
              <span v-if="rollSub(data.data)" class="pl-sub pl-num">{{ rollSub(data.data) }}</span>
            </div>
          </template>
          <template #meldungZelle="{ data }">
            <span
              v-if="data.data.messages.length"
              class="pl-msg"
              :class="'pl-msg--' + data.data.message_level"
              :title="data.data.message_text"
            >
              <app-icon name="alertTriangle" :size="14" class="pl-msg__icon" />
              <span class="pl-ellipsis">{{ data.data.messages[0].text }}</span>
              <span v-if="data.data.messages.length > 1" class="pl-msg__more">+{{ data.data.messages.length - 1 }}</span>
            </span>
          </template>
          <template #pfeilZelle>
            <app-icon name="chevR" :size="16" class="pl-pfeil" />
          </template>
        </dx-data-grid>

        <app-state-overlay v-if="overlayState === 'loading'" state="loading" text="Druckprotokoll wird geladen …" />
        <app-state-overlay
          v-else-if="overlayState === 'error'"
          state="error"
          title="Druckprotokoll konnte nicht geladen werden."
          @retry="load(true)"
        />
        <app-state-overlay
          v-else-if="overlayState === 'empty'"
          state="empty"
          icon="printer"
          title="Keine Einträge in diesem Zeitraum"
          text="Das Druckprotokoll zeigt Verkäufe sowie Drucke ohne Verkauf (Testdruck, Abrechnung, Fahrpläne, Dokumente) und Meldungen der Drucker, die die Drucker-App ab Version 3.0.5 hochlädt."
        />
        <app-state-overlay
          v-else-if="overlayState === 'filtered'"
          state="empty"
          :icon="query ? 'search' : 'funnel'"
          :title="query ? 'Keine Einträge für diese Suche' : 'Keine Einträge für diesen Filter'"
          :text="filterText"
        >
          <app-button @click="resetFilters">{{ query ? 'Suche und Filter zurücksetzen' : 'Filter zurücksetzen' }}</app-button>
        </app-state-overlay>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span v-if="narrowed && rows.length"><strong>{{ number(summary.total) }}</strong> von {{ number(rows.length) }} {{ plural(rows.length, 'Eintrag', 'Einträgen') }}</span>
          <span v-else><strong>{{ number(summary.total) }}</strong> {{ plural(summary.total, 'Eintrag', 'Einträge') }}</span>
          <!-- Handy: Aufteilung in eigener Zeile ohne Trenner (wie „davon … storniert“ bei Verkäufen) -->
          <template v-if="breakdown">
            <span v-if="!handy" class="app-sep" aria-hidden="true">·</span>
            <span :class="{ 'pl-fuss-zeile': handy }">{{ breakdown }}</span>
          </template>
          <template v-if="summary.messages">
            <span v-if="!handy" class="app-sep" aria-hidden="true">·</span>
            <span class="pl-fuss-meldung" :class="{ 'pl-fuss-zeile': handy }"><strong>{{ number(summary.messages) }}</strong> mit Meldung</span>
          </template>
        </template>
      </app-grid-footer>
      <!-- Hinweis, wenn der gewählte Zeitraum gekürzt wurde (die Schnittstelle liefert höchstens 62 Tage), und Fehler
           beim Aktualisieren (der bisherige Stand bleibt stehen) -->
      <app-toast
        v-model="toast"
        :text="toastText"
        :icon="toastError ? 'alert' : 'info'"
        :kind="toastError ? 'error' : 'info'"
        closable
      />
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!selected"
        :icon="detailIcon"
        :title="selected ? selected.kind_label : ''"
        :subtitle="selected ? detailSubtitle : ''"
        @close="closeDetail"
        @layout-change="onDetailLayout"
      >
        <print-log-detail v-if="selected" :entry="selected" :user-label="userLabel(selected)" />
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
// Verwaltung → Druckprotokoll (Kundenwunsch 05.11.2026, wörtlich „noch eine extra tabelle so wie verkäufe einzuführen,
// auf denen alle drucke zu sehen sind? wann wo von wem etc? als extra ansicht unter rollen. und eventuell
// fahrerabrechnung zuerst dann Rollen dann Druckerlog“ und „verkäufe in der ansicht nur vereinfacht darstellen
// logischerweise“). Alle Drucke eines Zeitraums in einer Tabelle wie Verkäufe: Verkäufe (vereinfacht: Fahrkarte, Preis,
// Drucker, Benutzer, Ort, Rolle – alles Weitere unter Verkäufe), Drucke ohne Verkauf und Meldungen der Drucker
// (verbunden, Papiereinzug, Deckel, Papier leer, Störung, getrennt). Daten: ticketsale?option=printLog (bconn-aws,
// höchstens 62 Tage), Aufbereitung in src/components/printlog/printLog.js, Arten in printKinds.js (gemeinsam mit Rollen).
// Filter (Drucker, Art) und Suche wirken auf die geladenen Zeilen; Detail im Detailbereich wie bei Verkäufen. Handy:
// Tabelle in natürlicher Höhe mit Zeit, Art und Bezeichnung (darunter Meldung bzw. Preis · Drucker · Benutzer).
import { DxDataGrid, DxPaging, DxPager, DxSorting, DxSelection } from 'devextreme-vue/data-grid';
import deMessages from 'devextreme/localization/messages/de.json';
import { locale, loadMessages } from 'devextreme/localization';
import axios from 'axios';
import moment from 'moment';
import PeriodField from '@/components/sales/PeriodField';
import PrintLogDetail from '@/components/printlog/PrintLogDetail';
import {
  normalizeRows,
  matchesKind,
  matchesPrinter,
  matchesSearch,
  kindItems,
  printerItems,
  summarize,
  periodPresets,
  clampPeriod,
  isGebe,
  today,
  MAX_DAYS
} from '@/components/printlog/printLog';
import { foldText, naturalKey } from '@/components/sales/saleFilters';
import DataGridMixin from '@/mixins/DataGridMixin';
import { formatEuro, formatNumber, plural, LEER } from '@/utils/format';

export default {
  name: 'PrintLog',
  components: { DxDataGrid, DxPaging, DxPager, DxSorting, DxSelection, PeriodField, PrintLogDetail },
  mixins: [DataGridMixin],
  data() {
    const day = today();
    return {
      fromDate: day,
      toDate: day,
      // geladene Zeilen (eingefroren, normalizeRows); loaded: Zeilen gehören zum gewählten Zeitraum
      rows: Object.freeze([]),
      loaded: false,
      loading: false,
      error: false,
      loadedAt: null,
      searchText: '',
      printerFilter: null,
      kindFilter: null,
      selected: null,
      users: [],
      gridAttr: { class: 'grid-2z' },
      toast: false,
      toastText: '',
      toastError: false,
      leer: LEER
    };
  },
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: natürliche Höhe (Seite scrollt), sonst füllt das Grid den Bereich
    gridHeight() {
      return this.handy ? undefined : '100%';
    },
    presets() {
      return periodPresets(this.loadedAt || new Date());
    },
    stand() {
      return this.loadedAt ? moment(this.loadedAt).format('HH:mm') + ' Uhr' : '';
    },
    crossesYear() {
      return String(this.fromDate).substring(0, 4) !== String(this.toDate).substring(0, 4);
    },
    // Anmeldename → voller Name (Benutzerliste des Mandanten)
    userNames() {
      const names = {};
      (this.users || []).forEach((u) => {
        if (u && u.username && u.fullname) {
          names[u.username] = u.fullname;
        }
      });
      return names;
    },
    query() {
      return foldText(String(this.searchText || '').trim());
    },
    narrowed() {
      return !!(this.printerFilter || this.kindFilter || this.query);
    },
    printerRows() {
      return this.printerFilter ? this.rows.filter((row) => matchesPrinter(row, this.printerFilter)) : this.rows;
    },
    shownRows() {
      const kind = this.kindFilter;
      const q = this.query;
      return Object.freeze(this.printerRows.filter((row) => matchesKind(row, kind) && matchesSearch(row, q, this.userLabel)));
    },
    printerFilterItems() {
      return printerItems(this.rows, this.printerFilter);
    },
    // Anzahl je Art nach dem gewählten Drucker
    kindFilterItems() {
      return kindItems(this.printerRows, this.kindFilter);
    },
    summary() {
      return summarize(this.shownRows);
    },
    // „120 Verkäufe · 8 ohne Verkauf · 5 Druckerzustände“ (nur vorhandene Gruppen, erst ab zwei Gruppen)
    breakdown() {
      const s = this.summary;
      const parts = [];
      if (s.sale) {
        parts.push(formatNumber(s.sale) + ' ' + plural(s.sale, 'Verkauf', 'Verkäufe'));
      }
      if (s.print) {
        parts.push(formatNumber(s.print) + ' ohne Verkauf');
      }
      if (s.state) {
        parts.push(formatNumber(s.state) + ' ' + plural(s.state, 'Druckerzustand', 'Druckerzustände'));
      }
      if (s.other) {
        parts.push(formatNumber(s.other) + ' ' + plural(s.other, 'sonstiger', 'sonstige'));
      }
      return parts.length > 1 ? parts.join(' · ') : '';
    },
    // Spalte Rolle nur, wenn im Zeitraum ein GeBE-Drucker oder eine Rollennummer vorkommt; Meldung nur mit Meldungen
    showRolls() {
      return this.rows.some((row) => row.roll_groups.length || row.paper_text || isGebe(row));
    },
    showMessages() {
      return this.rows.some((row) => row.messages.length);
    },
    columns() {
      const desk = !this.handy;
      return [
        {
          name: 'zeit', dataField: 'time', caption: 'Zeit', width: desk ? 104 : 84,
          sortOrder: 'desc', calculateSortValue: 'seq', cellTemplate: 'zeitZelle'
        },
        {
          name: 'art', dataField: 'kind_label', caption: 'Art', width: desk ? 184 : 136,
          calculateSortValue: (row) => foldText(row.kind_label), cellTemplate: 'artZelle'
        },
        {
          name: 'bezeichnung', dataField: 'description', caption: 'Bezeichnung', minWidth: desk ? 150 : 120,
          calculateSortValue: (row) => foldText(row.description), cellTemplate: 'bezZelle'
        },
        {
          name: 'drucker', dataField: 'printer_id', caption: 'Drucker', width: 116, visible: desk,
          calculateSortValue: (row) => naturalKey(row.printer_id), cellTemplate: 'druckerZelle'
        },
        {
          name: 'benutzer', dataField: 'username', caption: 'Benutzer', width: 124, visible: desk,
          calculateSortValue: (row) => foldText(this.userLabel(row)), cellTemplate: 'benutzerZelle'
        },
        {
          name: 'ort', dataField: 'stop_name', caption: 'Ort', minWidth: 140, visible: desk,
          calculateSortValue: (row) => foldText(row.stop_name), cellTemplate: 'ortZelle'
        },
        {
          name: 'rolle', dataField: 'roll_text', caption: 'Rolle/Abschnitte', width: 152, visible: desk && this.showRolls,
          cellTemplate: 'rolleZelle'
        },
        {
          name: 'meldung', dataField: 'message_text', caption: 'Meldung', width: 168, visible: desk && this.showMessages,
          cellTemplate: 'meldungZelle'
        },
        // › nur bei Touch (wie Verkäufe)
        {
          name: 'pfeil', caption: '', width: 32, allowSorting: false, visible: this.gridTouch,
          cssClass: 'pl-pfeilspalte', cellTemplate: 'pfeilZelle'
        }
      ];
    },
    overlayState() {
      if (!this.loaded) {
        return this.error ? 'error' : 'loading';
      }
      if (!this.rows.length) {
        return 'empty';
      }
      return this.shownRows.length ? null : 'filtered';
    },
    filterText() {
      const parts = [];
      const printer = this.printerFilterItems.find((item) => item.value === this.printerFilter);
      const kind = this.kindFilterItems.find((item) => item.value === this.kindFilter);
      if (this.printerFilter && printer) {
        parts.push('Drucker: ' + (printer.ariaText || printer.buttonText || printer.text));
      }
      if (this.kindFilter && kind) {
        parts.push('Art: ' + (kind.buttonText || kind.text));
      }
      if (this.query) {
        parts.push('Suche: „' + String(this.searchText).trim() + '“');
      }
      return parts.join(' · ');
    },
    detailIcon() {
      if (!this.selected) {
        return 'printer';
      }
      if (this.selected.tone === 'warn' || this.selected.tone === 'error') {
        return 'alertTriangle';
      }
      return this.selected.group === 'sale' ? 'receipt' : 'printer';
    },
    detailSubtitle() {
      const m = moment(this.selected.time);
      return m.isValid() ? m.format(this.crossesYear ? 'DD.MM.YYYY, HH:mm' : 'DD.MM., HH:mm') : '';
    }
  },
  watch: {
    // eingeschränkt (Filter, Suche, neue Daten): Detail schließen, wenn sein Eintrag nicht mehr angezeigt wird
    shownRows(rows) {
      if (this.selected && !rows.some((row) => row.id === this.selected.id)) {
        this.closeDetail();
      }
    },
    handy() {
      this.$nextTick(this.updateDimensions);
    }
  },
  created() {
    loadMessages(deMessages);
    locale('de'); // immer Deutsch wie src/plugins/devextreme.js
    // nicht reaktiv: laufende Anfrage (spätere Antworten gewinnen), Detail schließt
    this._request = 0;
    this._closing = false;
    this._closingTimer = null;
  },
  mounted() {
    this.load(true);
    this.loadUsers();
  },
  beforeDestroy() {
    clearTimeout(this._closingTimer);
  },
  methods: {
    // reset: neuer Zeitraum – alte Zeilen verwerfen (Ladezustand); sonst Aktualisieren mit stehendem Stand
    async load(reset) {
      if (this.loading && !reset) {
        return;
      }
      const request = ++this._request;
      if (reset) {
        this.loaded = false;
        this.rows = Object.freeze([]);
      }
      this.loading = true;
      this.error = false;
      try {
        const response = await axios.get(
          `${this.$store.state.baseUrl}ticketsale?option=printLog&from=${this.fromDate}&to=${this.toDate}`
        );
        if (request !== this._request) {
          return;
        }
        const data = response && response.data ? response.data : {};
        this.rows = normalizeRows(data.rows);
        this.loaded = true;
        this.loadedAt = new Date();
        // offenes Detail mit dem neuen Stand derselben Zeile
        if (this.selected) {
          const same = this.rows.find((row) => row.id === this.selected.id);
          if (same) {
            this.selected = same;
          }
        }
      } catch (e) {
        if (request === this._request) {
          this.error = true;
          // Aktualisieren mit stehendem Stand: Meldung statt Fehlerzustand
          if (this.loaded) {
            this.toastText = 'Aktualisieren fehlgeschlagen. Bitte erneut versuchen.';
            this.toastError = true;
            this.toast = true;
          }
        }
      } finally {
        if (request === this._request) {
          this.loading = false;
        }
      }
    },
    async loadUsers() {
      try {
        const response = await axios.get(
          `${this.$store.state.baseUrl}user?customer_id=${this.$store.state.customerId}`
        );
        this.users = Array.isArray(response.data) ? response.data : [];
      } catch (e) {
        // ohne Benutzerliste steht der Anmeldename da
        this.users = [];
      }
    },
    onPeriodApply(period) {
      const range = clampPeriod(period.from, period.to);
      if (range.clamped) {
        this.toastError = false;
        this.toastText = 'Das Druckprotokoll zeigt höchstens ' + MAX_DAYS + ' Tage: '
          + moment(range.from).format('DD.MM.YYYY') + ' bis ' + moment(range.to).format('DD.MM.YYYY') + '.';
        this.toast = true;
      }
      if (range.from === this.fromDate && range.to === this.toDate) {
        this.load(false);
        return;
      }
      this.fromDate = range.from;
      this.toDate = range.to;
      this.closeDetail();
      this.load(true);
    },
    resetFilters() {
      this.printerFilter = null;
      this.kindFilter = null;
      this.searchText = '';
    },
    // voller Name, sonst Anmeldename, sonst Benutzer-ID; Meldungen ohne angemeldeten Benutzer leer
    userLabel(row) {
      if (!row) {
        return '';
      }
      if (row.username) {
        return this.userNames[row.username] || row.username;
      }
      return row.user_id !== null && row.user_id !== undefined ? 'Benutzer ' + row.user_id : '';
    },
    getGrid() {
      const ref = this.$refs.grid;
      return ref && ref.instance ? ref.instance : null;
    },
    updateDimensions() {
      const grid = this.getGrid();
      if (grid) {
        grid.updateDimensions();
      }
    },
    onContentReady(e) {
      if (this.$refs.fuss) {
        this.$refs.fuss.update(e.component);
      }
    },
    onFocusedRowChanged(e) {
      if (this._closing || !e.row || e.row.rowType !== 'data') {
        return;
      }
      this.selected = e.row.data;
    },
    // wie Verkäufe: DevExtreme stellt den Fokus der zuletzt gewählten Zeile kurz nach dem Schließen wieder her – das darf
    // das Detail nicht erneut öffnen
    closeDetail() {
      const wasOpen = !!this.selected;
      this.selected = null;
      this.resetFocusedRow();
      if (wasOpen) {
        this._closing = true;
        clearTimeout(this._closingTimer);
        this._closingTimer = setTimeout(() => {
          this._closing = false;
          this.resetFocusedRow();
        }, 300);
      }
    },
    resetFocusedRow() {
      const grid = this.getGrid();
      if (grid && grid.option('focusedRowIndex') !== -1) {
        grid.option('focusedRowIndex', -1);
      }
    },
    onDetailLayout() {
      this.updateDimensions();
    },
    clockText(time) {
      const m = time ? moment(time) : null;
      return m && m.isValid() ? m.format('HH:mm:ss') : LEER;
    },
    dayText(time) {
      const m = time ? moment(time) : null;
      if (!m || !m.isValid()) {
        return '';
      }
      return this.crossesYear ? m.format('DD.MM.YYYY') : m.locale('de').format('dd, DD.MM.');
    },
    rollSub(row) {
      if (row.paper_text) {
        return row.paper_text;
      }
      return row.group === 'state' && row.roll_text ? 'gelesene Stelle' : '';
    },
    phoneLine(row) {
      if (row.messages.length) {
        return row.message_text;
      }
      return [
        row.source === 'sale' && row.price !== null ? formatEuro(row.price) : null,
        row.printer_id !== null ? 'Drucker ' + row.printer_id : null,
        this.userLabel(row) || null
      ].filter(Boolean).join(' · ');
    },
    euro(value) {
      return formatEuro(value);
    },
    number(value) {
      return formatNumber(value);
    },
    plural(n, one, many) {
      return plural(n, one, many);
    }
  }
};
</script>

<style scoped>
.pl-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* Handy: natürliche Höhe statt fester Fläche, Platz für Lade- und Leerzustand */
.pl-gridcol--natural .app-grid-fill {
  flex: none;
}
.pl-gridcol--natural .pl-gridfill--leer {
  min-height: 260px;
}
.pl-meta {
  min-width: 0;
  margin-left: 4px;
  font-size: 13px;
  line-height: 18px;
  color: var(--ink-3);
  white-space: nowrap;
}
/* Handy: Suche in eigener Zeile unter dem Zeitraum (wie Verkäufe) */
.pl-page.app-page--phone .pl-search {
  flex-basis: 100% !important;
  width: auto !important;
  max-width: none;
}
/* Bedienelemente rechtsbündig (Handy linksbündig wie die Knopfzeile) */
.pl-page:not(.app-page--phone) ::v-deep .app-page__toolbar-end {
  justify-content: flex-end;
}
.pl-fuss-zeile {
  flex-basis: 100%;
}
.pl-fuss-meldung strong {
  color: var(--red);
}
/* Zellen (DevExtreme-Vorlagen) */
.pl-gridcol ::v-deep .pl-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.pl-gridcol ::v-deep .pl-ellipsis {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pl-gridcol ::v-deep .pl-main {
  font-weight: 500;
}
.pl-gridcol ::v-deep .pl-sub {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.pl-gridcol ::v-deep .pl-sub--error {
  color: var(--red);
}
.pl-gridcol ::v-deep .pl-sub--warn {
  color: var(--amber);
}
.pl-gridcol ::v-deep .pl-leer {
  color: var(--ink-4);
  font-weight: 400;
}
.pl-gridcol ::v-deep .pl-num {
  font-variant-numeric: tabular-nums;
}
/* Art: Punkt und Text; Verkauf ohne Punktfarbe (neutral), Storno rot, Druck ohne Verkauf blau, Drucker bereit
   (verbunden, Papiereinzug) grün, Drucker getrennt bzw. Störung rot, Warnung (Papier leer, Deckel) gelb, übrige
   Zustände grau */
.pl-gridcol ::v-deep .pl-kind {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  color: var(--ink);
}
.pl-gridcol ::v-deep .pl-kind__dot {
  flex: none;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1.5px solid var(--edge);
}
.pl-gridcol ::v-deep .pl-kind--storno {
  color: var(--red);
}
.pl-gridcol ::v-deep .pl-kind--storno .pl-kind__dot {
  border: 0;
  background: var(--st-late);
}
.pl-gridcol ::v-deep .pl-kind--print .pl-kind__dot {
  border: 0;
  background: var(--d-std);
}
.pl-gridcol ::v-deep .pl-kind--state {
  color: var(--ink-2);
}
.pl-gridcol ::v-deep .pl-kind--state .pl-kind__dot {
  border: 0;
  background: var(--st-none);
}
.pl-gridcol ::v-deep .pl-kind--ok {
  color: var(--green-fg);
}
.pl-gridcol ::v-deep .pl-kind--ok .pl-kind__dot {
  border: 0;
  background: var(--st-ok);
}
.pl-gridcol ::v-deep .pl-kind--error {
  color: var(--red);
  font-weight: 600;
}
.pl-gridcol ::v-deep .pl-kind--error .pl-kind__dot {
  border: 0;
  background: var(--st-late);
}
.pl-gridcol ::v-deep .pl-kind--warn {
  color: var(--amber);
  font-weight: 600;
}
.pl-gridcol ::v-deep .pl-kind--warn .pl-kind__dot {
  border: 0;
  background: var(--st-warn);
}
/* Ort mit Kartenlink */
.pl-gridcol ::v-deep .pl-ort {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.pl-gridcol ::v-deep .pl-map {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: var(--r-ctl);
  color: var(--link);
}
:where(html:not(.dichte-touch)) .pl-gridcol ::v-deep .pl-map:hover {
  background: var(--sel);
}
.pl-gridcol ::v-deep .pl-map:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--link);
}
/* Meldung: Fehler beim Druck rot, Hinweise gelb */
.pl-gridcol ::v-deep .pl-msg {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
  font-weight: 500;
}
.pl-gridcol ::v-deep .pl-msg__icon {
  flex: none;
}
.pl-gridcol ::v-deep .pl-msg--error {
  color: var(--red);
}
.pl-gridcol ::v-deep .pl-msg--warn {
  color: var(--amber);
}
.pl-gridcol ::v-deep .pl-msg__more {
  flex: none;
  font-size: 12px;
  color: var(--ink-3);
}
.pl-gridcol ::v-deep .pl-pfeil {
  display: block;
  color: var(--ink-3);
}
/* Endspalte › (Touch): 32 breit, Polster 0 12 0 4 wie Verkäufe */
.pl-gridcol ::v-deep .dx-datagrid .dx-datagrid-rowsview .dx-data-row > td.pl-pfeilspalte:not([class*="dx-command"]),
.pl-gridcol ::v-deep .dx-datagrid .dx-datagrid-headers .dx-header-row > td.pl-pfeilspalte:not([class*="dx-command"]) {
  padding: 0 12px 0 4px;
}
</style>
